/* eslint-disable react-hooks/exhaustive-deps */

import { Box, Button, Flex, Stack, Text, Textarea } from '@chakra-ui/react';
import { Skeleton } from '@/components/ui/skeleton';
import { fetchAPI } from '@/api/fetch';
import { apiAdminGetPrompt, apiAdminUpdatePrompt } from '@/api/services';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { PromptKey } from '@/types/enums';

type FormValues = {
  promptText: string;
};

export default function AnalyzerEditor({ promptKey }: { promptKey: PromptKey }) {
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting, isValid }
  } = useForm<FormValues>();

  useEffect(() => {
    const fetchPrompt = async () => {
      try {
        const response = await fetchAPI<Prompt>({
          request: () => apiAdminGetPrompt({ key: promptKey })
        });

        if (response.success) {
          setPrompt(response.data);
          reset({ promptText: response.data.prompt_text }, { keepDefaultValues: false });
        } else {
          console.error('Error fetching prompt:', response.data);
          setFetchError('Unable to retrieve prompt.');
        }
      } catch (error) {
        console.error('Error fetching prompt:', error);
        setFetchError('Unable to retrieve prompt.');
      }
    };

    fetchPrompt();
  }, [promptKey]);

  const handleFormSubmit = async (formData: FormValues) => {
    setFetchError(null);

    try {
      const response = await fetchAPI<Prompt>({
        request: () =>
          apiAdminUpdatePrompt({
            key: promptKey,
            payload: {
              prompt_text: formData.promptText
            }
          })
      });
      if (response.success) {
        setPrompt(response.data);
      } else {
        console.error('Error updating prompt:', response.message);
        setFetchError('Unable to update prompt.');
      }
    } catch (error) {
      console.error('Error updating prompt:', error);
      setFetchError('Unable to update prompt.');
    }
  };

  return (
    <Box>
      {prompt ? (
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Stack h="100%">
            <Textarea
              p="16px 24px"
              placeholder="Enter prompt here..."
              border="1px solid"
              borderColor={errors.promptText ? 'error.500' : 'neutrals.200'}
              bg="background.secondary"
              fontSize="16px"
              autoresize
              minH="calc(100vh - 250px)"
              maxH="calc(100vh - 250px)"
              borderRadius="12px"
              key={promptKey}
              {...register('promptText', {
                required: 'Prompt text is required.'
              })}
            />
            {errors.promptText && (
              <Text fontSize="14px" color="error.700" mt="5px">
                {errors.promptText.message}
              </Text>
            )}
            {fetchError && (
              <Text fontSize="16px" color="error.700" mt="5px">
                {fetchError}
              </Text>
            )}
            <Flex justify="flex-end" gap="20px" mt="24px">
              <Button
                type="button"
                bg="background.secondary"
                color="primary.900"
                fontSize="16px"
                border="1px solid"
                borderColor="primary.900"
                borderRadius="8px"
                p="24px 32px"
                w="150px"
                onClick={() => reset()}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={isSubmitting}
                disabled={!isValid}
                bg="primary.900"
                fontSize="16px"
                borderRadius="8px"
                p="24px 32px"
                w="150px"
              >
                Submit
              </Button>
            </Flex>
          </Stack>
        </form>
      ) : (
        <Stack>
          <Skeleton borderRadius="8px" h="calc(100vh - 250px)" w="100%" />
          {fetchError && (
            <Text fontSize="16px" color="error.700">
              {fetchError}
            </Text>
          )}
        </Stack>
      )}
    </Box>
  );
}
